import { ComponentType, ElementType } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { SideNavLink } from "@carbon/react";

/**
 * NOT using `createCustomSideNavLink` here. It is declared in
 * @carbon/react's type definitions (SideNavLink.d.ts) but has no matching
 * runtime export in the installed 1.115.0 build — confirmed absent from
 * both the CJS (`lib`) and ESM (`es`) bundles, at the package root and at
 * `@carbon/react/lib/components/UIShell` (which has no runtime index at
 * all, only a `.d.ts`). Importing it would fail at build/runtime despite
 * type-checking.
 *
 * Instead this uses Carbon's actual, shipped mechanism for the same job:
 * `SideNavLink`'s underlying `Link` accepts an `as` prop specifically
 * "to support use-cases like react-router" (see its own JSDoc). Passing
 * `as={Link}` achieves the identical outcome — a Carbon SideNavLink
 * rendered through React Router's `Link`, no full-page reload — without
 * needing a factory. `Link` is already a stable module-level reference,
 * so there is no per-render remount risk to guard against here.
 */

export interface NavItem {
  /** An i18n key (e.g. "navigation.home"), translated at render — NOT a pre-translated string, so
   * the sidebar re-translates live when the language changes. See utils/navigation.ts. */
  name: string;
  href: string;
  icon: ElementType;
  /**
   * Legacy field from utils/navigation.ts. Deliberately NOT read: it is
   * hardcoded to Home in all nine configs, which is why aria-current was
   * wrong on every page. Active state comes from the route below.
   */
  current?: boolean;
}

/**
 * Home matches only the exact root. Without this, every route would light up
 * Home, since every href begins with "/".
 */
const isActive = (pathname: string, href: string) =>
  href === "/" ? pathname === "/" : pathname === href;

interface AppShellNavProps {
  navigation: NavItem[];
  /** External links (support, etc). Rendered as plain anchors, not router links. */
  secondary?: NavItem[];
}

const AppShellNav = ({ navigation, secondary }: AppShellNavProps) => {
  // `useTranslation` subscribes this component to i18next's languageChanged event, so switching
  // language re-renders the sidebar and re-translates every key below — no reload needed.
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <>
      {navigation.map((item) => (
        <SideNavLink
          key={item.href}
          as={Link}
          to={item.href}
          renderIcon={item.icon as ComponentType}
          aria-current={isActive(pathname, item.href) ? "page" : undefined}
        >
          {t(item.name)}
        </SideNavLink>
      ))}

      {secondary?.map((item) => (
        <SideNavLink
          key={item.href}
          href={item.href}
          renderIcon={item.icon as ComponentType}
          target="_blank"
          rel="noreferrer"
        >
          {t(item.name)}
        </SideNavLink>
      ))}
    </>
  );
};

export default AppShellNav;
